/**
 * ProviderScheduleTab Component
 * 
 * Displays the provider's upcoming appointments and availability settings
 * Shows up to 20 upcoming appointments with attendee information
 */
import React from 'react';
import { useRecordContext } from 'react-admin';
import { Box, Typography, List, ListItem, ListItemText, Alert, Chip, Divider } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const ProviderScheduleTab = () => {
    const record = useRecordContext();
    const healthieData = record?.healthie_data;
    const upcomingAppointments = healthieData?.upcoming_appointments || [];
    const appointmentSetting = healthieData?.appointment_setting;
    const locations = appointmentSetting?.appointment_locations || [];

    return (
        <Box sx={{ p: 2 }}>
            {/* Appointment Locations Section */}
            {locations.length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <LocationOnIcon color="primary" />
                        <Typography variant="h6">
                            Service Locations
                        </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Zip codes and appointment types this provider offers
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {locations.map((loc) => (
                            <Chip
                                key={loc.id}
                                label={loc.location}
                                variant="outlined"
                                color="primary"
                                icon={<LocationOnIcon />}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {locations.length > 0 && upcomingAppointments.length > 0 && <Divider sx={{ my: 3 }} />}

            {/* Upcoming Appointments Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <EventIcon color="primary" />
                <Typography variant="h6">
                    Upcoming Appointments ({upcomingAppointments.length})
                </Typography>
            </Box>

            {upcomingAppointments.length === 0 ? (
                <Alert severity="info" icon={<InfoOutlinedIcon />}>
                    <Typography variant="body2">
                        No upcoming appointments scheduled.
                    </Typography>
                </Alert>
            ) : (
                <>
                    <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                        {upcomingAppointments.map((appt, index) => {
                            const appointmentDate = new Date(appt.date);
                            const isToday = appointmentDate.toDateString() === new Date().toDateString();
                            const attendeeNames = appt.attendees?.map(a => a.full_name).join(', ') || 'No attendees';

                            return (
                                <ListItem
                                    key={appt.id}
                                    sx={{
                                        borderBottom: index < upcomingAppointments.length - 1 ? '1px solid #eee' : 'none',
                                        py: 2,
                                        bgcolor: isToday ? 'action.hover' : 'transparent'
                                    }}
                                >
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {appointmentDate.toLocaleDateString('en-US', {
                                                        weekday: 'short',
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric'
                                                    })}
                                                </Typography>
                                                <Typography variant="body1" color="text.secondary">
                                                    {appointmentDate.toLocaleTimeString('en-US', {
                                                        hour: 'numeric',
                                                        minute: '2-digit'
                                                    })}
                                                </Typography>
                                                {isToday && (
                                                    <Chip
                                                        label="Today"
                                                        size="small"
                                                        color="warning"
                                                    />
                                                )}
                                                {appt.pm_status && (
                                                    <Chip
                                                        label={appt.pm_status}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            <Box>
                                                <Typography variant="body2" color="text.primary">
                                                    {appt.appointment_type?.name || 'Appointment'}
                                                    {appt.length && (
                                                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                            ({appt.length} min)
                                                        </Typography>
                                                    )}
                                                </Typography>
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    With: {attendeeNames}
                                                </Typography>
                                                {appt.location && (
                                                    <Typography variant="caption" display="block" color="text.secondary">
                                                        Location: {appt.location}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                    />
                                </ListItem>
                            );
                        })}
                    </List>

                    {upcomingAppointments.length >= 50 && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="caption">
                                Showing next 50 appointments. View Healthie EHR for complete schedule.
                            </Typography>
                        </Alert>
                    )}
                </>
            )}
        </Box>
    );
};

export default ProviderScheduleTab;

